// context/playercontext.js — Global persistent audio player context for Nativus
import React, { createContext, useContext, useRef, useState, useCallback, useEffect } from 'react';
import { trackPlay, newListenSession, clearListenSession } from '../services/analytics';
import { fetchAudioUrl, clearListCaches } from '../api/api';
import { useAuth } from './authcontext';
import { usePremiumPopup } from '../components/PremiumPopup';
import * as Sentry from '@sentry/react';
import { logger } from '../utils/logger';

const PlayerContext = createContext(null);

export const usePlayer = () => {
  const ctx = useContext(PlayerContext);
  if (!ctx) throw new Error('usePlayer must be used within PlayerProvider');
  return ctx;
};

export const PlayerProvider = ({ children }) => {
  const { user } = useAuth();
  const { triggerPremiumPopup } = usePremiumPopup();

  // ── Audio state ──────────────────────────────────────────────────────────────────
  const [currentMeditation, setCurrentMeditation] = useState(null);
  const [tehniciMapGlobal, setTehniciMapGlobal] = useState({});
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [audioUrl, setAudioUrl] = useState('');
  const [feedbackReadyForId, setFeedbackReadyForId] = useState(null);
  // Eroare la incarcarea audio — expusa in UI ca sa stie user-ul ca a
  // existat o problema (ex. conexiune instabila, 403/404 pe semnatura).
  // { type: 'network'|'premium'|'notfound'|'unknown', status?: number }
  const [audioError, setAudioError] = useState(null);

  // ── Refs ────────────────────────────────────────────────────────────────────────
  const audioRef = useRef(null);
  const canSeekRef = useRef(false);
  const playStartedTrackedRef = useRef(false);
  const completedTrackedRef = useRef(false);
  const lastMilestoneTrackedRef = useRef(0);
  const totalListenedDurationRef = useRef(0);
  const playStartTimeRef = useRef(null);
  const isTrackingClosedRef = useRef(false);
  const currentMeditationIdRef = useRef(null);
  const lastTrackedMeditationIdRef = useRef(null);
  const lastListenSessionIdRef = useRef(null);
  const lastDurationSecRef = useRef(0);
  const currentMeditationObjRef = useRef(null);
  // Ultimul listen_percent cunoscut (setat la timeupdate/pause). Folosit ca
  // fallback la `stop` daca acumularea a esuat (ex. timeupdate suspendat in
  // background pe mobil).
  const lastKnownListenPercentRef = useRef(0);
  // Tracks the meditation ID for which audio was successfully fetched and loaded.
  // Used to allow re-loading the same meditation after a premium upgrade
  // (audioRef.current.src resolves empty strings to the page URL, making it always truthy).
  const audioLoadedForMedRef = useRef(null);

  useEffect(() => {
    currentMeditationIdRef.current = currentMeditation?.id || null;
    if (currentMeditation?.id) {
      lastTrackedMeditationIdRef.current = currentMeditation.id;
    }
    currentMeditationObjRef.current = currentMeditation;
  }, [currentMeditation]);

  const getTrackingMeditationId = useCallback(() => {
    return currentMeditationIdRef.current || lastTrackedMeditationIdRef.current || null;
  }, []);

  const trackPlaySafe = useCallback((eventMedId, eventName, payload) => {
    if (!eventMedId) return false;
    trackPlay(eventMedId, eventName, payload);
    return true;
  }, []);

  const resetTrackingRefs = useCallback(() => {
    playStartedTrackedRef.current = false;
    completedTrackedRef.current = false;
    lastMilestoneTrackedRef.current = 0;
    totalListenedDurationRef.current = 0;
    playStartTimeRef.current = null;
    isTrackingClosedRef.current = false;
    canSeekRef.current = false;
    lastDurationSecRef.current = 0;
    audioLoadedForMedRef.current = null;
    lastKnownListenPercentRef.current = 0;
  }, []);

  const sendMeditationClosed = useCallback(() => {
    const medId = getTrackingMeditationId();
    if (!isTrackingClosedRef.current && medId) {
      const audioElement = audioRef.current;
      const currentPlayStartTime = playStartTimeRef.current;
      const currentTotalListened = totalListenedDurationRef.current;

      const isCurrentlyPlaying = audioElement && !audioElement.paused;
      let finalTotalListened = currentTotalListened;
      if (audioElement && currentPlayStartTime !== null && isCurrentlyPlaying) {
        const ct = audioElement.currentTime || 0;
        const sessionDuration = Math.max(0, ct - currentPlayStartTime);
        finalTotalListened = currentTotalListened + sessionDuration;
      }

      const totalDuration = audioElement?.duration || lastDurationSecRef.current || 0;
      const totalListened = Math.round(finalTotalListened);

      // Fallback: daca acumularea a esuat (timeupdate suspendat pe mobil, etc.),
      // folosim ultima pozitie cunoscuta ca minimum. Astfel `stop` nu va mai
      // raporta 0% cand pause-ul precedent a raportat >0%.
      const accumulatedPct = totalDuration > 0 ? Math.round((totalListened / totalDuration) * 100) : 0;
      const positionPct = audioElement && totalDuration > 0
        ? Math.round(((audioElement.currentTime || 0) / totalDuration) * 100)
        : 0;
      const listenPercent = Math.min(
        100,
        Math.max(accumulatedPct, lastKnownListenPercentRef.current || 0, positionPct)
      );

      if (totalDuration > 0 || totalListened > 0 || listenPercent > 0) {
        trackPlaySafe(medId, 'stop', {
          positionSec: totalListened,
          durationSec: Math.round(totalDuration || 0),
          listenPercent,
          listenedTotalSec: totalListened,
          listenSessionId: lastListenSessionIdRef.current || undefined,
        });
        isTrackingClosedRef.current = true;
      }
    }
  }, [getTrackingMeditationId, trackPlaySafe]);

  useEffect(() => {
    const handleBeforeUnload = () => sendMeditationClosed();
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [sendMeditationClosed]);

  const loadMeditation = useCallback(async (meditation, tehniciMap = {}, _beneficiiMap = {}) => {
    if (!meditation?.id) return;

    const isPremium = meditation.premium === true || meditation.premium === 'Da';
    const userIsPremium = user?.premium === true;
    if (isPremium && !userIsPremium) {
      // User free pe meditatie premium: pagina de detalii afiseaza paywall
      // singura (pe baza meditatiei din URL). NU atingem state-ul player-ului
      // ca sa nu avem leak: daca setam currentMeditation aici si lasam
      // audioRef.src de la meditatia anterioara, mini-playerul de jos ar
      // afisa coverul premium iar butonul play ar porni audio-ul vechi
      // (user-ul ar asculta "gratis" o alta meditatie crezand ca e cea premium).
      return;
    }

    // Skip reload only if audio was already successfully fetched for this meditation.
    // We intentionally avoid audioRef.current.src here because browsers resolve empty
    // src strings to the page URL, making it always truthy even when no audio is loaded.
    if (currentMeditationIdRef.current === meditation.id && audioLoadedForMedRef.current === meditation.id) {
      return;
    }

    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      // Evităm `src = ""` pentru că browserul îl resolve ca URL-ul paginii
      // curente și declanșează MEDIA_ELEMENT_ERROR „Empty src attribute".
      audioRef.current.removeAttribute('src');
      audioRef.current.load();
    }
    sendMeditationClosed();
    clearListenSession();

    currentMeditationIdRef.current = meditation.id;
    lastTrackedMeditationIdRef.current = meditation.id;
    lastListenSessionIdRef.current = null;
    lastDurationSecRef.current = 0;
    currentMeditationObjRef.current = meditation;
    setCurrentMeditation(meditation);
    setTehniciMapGlobal(tehniciMap);
    setIsPlaying(false);
    setProgress(0);
    setCurrentTime(0);
    setDuration(0);
    setAudioUrl('');
    setFeedbackReadyForId(null);
    setAudioError(null);
    resetTrackingRefs();

    if (!meditation.has_audio && !meditation.audio_path) {
      return;
    }

    try {
      const response = await fetchAudioUrl(meditation.id);
      if (response?.audioUrl) {
        const url = response.audioUrl;
        logger.info('[audio] loaded url for', meditation.id, '→', url);
        setAudioUrl(url);
        setAudioError(null);
        if (audioRef.current) {
          audioRef.current.src = url;
          audioRef.current.load();
          audioLoadedForMedRef.current = meditation.id;
        }
      } else {
        console.warn('[audio] empty response for', meditation.id, response);
        setAudioError({ type: 'unknown' });
      }
    } catch (err) {
      const status = err?.response?.status;
      console.error('[audio] fetchAudioUrl failed for', meditation.id, status, err?.message);
      // Clasificam eroarea ca UI-ul sa arate mesajul potrivit:
      //  - network / 5xx → conexiune instabila sau server down
      //  - 403 → premium required sau token invalid
      //  - 404 → meditatie/audio lipsa
      let type = 'unknown';
      if (!err?.response) type = 'network';
      else if (status === 403) type = 'premium';
      else if (status === 404) type = 'notfound';
      else if (status >= 500) type = 'network';
      setAudioError({ type, status: status || null });
      Sentry.captureException(err, { tags: { flow: 'audio_load' }, extra: { meditationId: meditation?.id, status } });
    }
  }, [user, sendMeditationClosed, resetTrackingRefs]);

  const playMeditation = useCallback(async (meditation, tehniciMap = {}, beneficiiMap = {}) => {
    await loadMeditation(meditation, tehniciMap, beneficiiMap);
    setTimeout(() => {
      if (audioRef.current?.src) {
        audioRef.current.play().catch(() => {});
      }
    }, 100);
  }, [loadMeditation]);

  const togglePlay = useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) {
      a.play().catch(() => {});
    } else {
      a.pause();
    }
  }, []);

  const skip = useCallback((seconds) => {
    const a = audioRef.current;
    if (!a || !a.duration || !canSeekRef.current) return;
    const next = Math.max(0, Math.min((a.currentTime || 0) + seconds, a.duration));
    a.currentTime = next;
  }, []);

  const seekTo = useCallback((ratio) => {
    const a = audioRef.current;
    if (!a || !a.duration) return;
    a.currentTime = Math.max(0, Math.min(ratio, 1)) * a.duration;
  }, []);

  const stopAndClear = useCallback(() => {
    const a = audioRef.current;
    if (a) {
      a.pause();
      a.currentTime = 0;
      a.removeAttribute('src');
      a.load();
    }
    sendMeditationClosed();
    clearListenSession();
    setCurrentMeditation(null);
    setTehniciMapGlobal({});
    setIsPlaying(false);
    setProgress(0);
    setCurrentTime(0);
    setDuration(0);
    setAudioUrl('');
    setFeedbackReadyForId(null);
    resetTrackingRefs();
  }, [sendMeditationClosed, resetTrackingRefs]);

  const formatDuration = useCallback((seconds) => {
    const mins = Math.floor((seconds || 0) / 60).toString().padStart(2, '0');
    const secs = Math.floor((seconds || 0) % 60).toString().padStart(2, '0');
    return `${mins}:${secs}`;
  }, []);

  const handleLoadedMetadata = useCallback((e) => {
    const a = e.currentTarget;
    setDuration(a.duration || 0);
    lastDurationSecRef.current = Math.round(a.duration || 0);
  }, []);

  const handleLoadedData = useCallback(() => {
    canSeekRef.current = true;
  }, []);

  const handleTimeUpdate = useCallback((e) => {
    const a = e.currentTarget;
    const d = a.duration || 0;
    const t = a.currentTime || 0;
    setCurrentTime(t);
    const progressPct = d ? (t / d) * 100 : 0;
    setProgress(progressPct);

    if (!a.paused && playStartTimeRef.current !== null) {
      const delta = t - playStartTimeRef.current;
      // Cap 30s: timeupdate poate fi suspendat in background pe mobil (pana
      // la ~15s pe Android). Cap-ul ne protejeaza doar de seek-uri mari.
      if (delta > 0 && delta < 30) {
        totalListenedDurationRef.current += delta;
      }
      playStartTimeRef.current = t;
    }

    if (d > 0) {
      lastKnownListenPercentRef.current = Math.min(100, Math.round(progressPct));
    }

    const medId = getTrackingMeditationId();
    if (medId && d > 0) {
      const milestones = [25, 50, 75, 100];
      const currentMilestone = milestones.find(
        (m) => progressPct >= m && lastMilestoneTrackedRef.current < m
      );
      if (currentMilestone) {
        trackPlaySafe(medId, 'pause', {
          positionSec: Math.round(totalListenedDurationRef.current),
          durationSec: Math.round(d),
          listenPercent: Math.round(progressPct),
          listenedTotalSec: Math.round(totalListenedDurationRef.current),
          listenSessionId: lastListenSessionIdRef.current || undefined,
        });
        lastMilestoneTrackedRef.current = currentMilestone;
      }
      if (progressPct >= 95 && !completedTrackedRef.current) {
        trackPlaySafe(medId, 'complete', {
          positionSec: Math.round(d),
          durationSec: Math.round(d),
          listenPercent: 100,
          listenedTotalSec: Math.round(totalListenedDurationRef.current),
          listenSessionId: lastListenSessionIdRef.current || undefined,
        });
        completedTrackedRef.current = true;
        setFeedbackReadyForId(medId);
      }
    }
  }, [getTrackingMeditationId, trackPlaySafe]);

  const handlePlay = useCallback(() => {
    setIsPlaying(true);
    if (audioRef.current) {
      playStartTimeRef.current = audioRef.current.currentTime || 0;
    }
    const medId = getTrackingMeditationId();
    if (!medId) return;
    if (!lastListenSessionIdRef.current) {
      lastListenSessionIdRef.current = newListenSession();
    }
    trackPlaySafe(medId, 'play', {
      positionSec: Math.round(audioRef.current?.currentTime || 0),
      durationSec: Math.round(audioRef.current?.duration || lastDurationSecRef.current || 0),
      listenPercent: 0,
      isRepeat: playStartedTrackedRef.current,
      listenedTotalSec: Math.round(totalListenedDurationRef.current),
      listenSessionId: lastListenSessionIdRef.current,
    });
    // Invalidăm cache-ul de home personalized: backend-ul exclude meditațiile
    // ascultate din `forYou` și le adaugă în `recent`. La următorul retur pe
    // home, hero-ul și „Ascultă din nou” trebuie să reflecte starea curentă.
    if (!playStartedTrackedRef.current && user?.id) {
      clearListCaches(`home-personalized:${user.id}`);
    }
    playStartedTrackedRef.current = true;
  }, [getTrackingMeditationId, trackPlaySafe, user?.id]);

  const handlePause = useCallback(() => {
    setIsPlaying(false);
    if (audioRef.current && playStartTimeRef.current !== null) {
      const delta = (audioRef.current.currentTime || 0) - playStartTimeRef.current;
      // Cap 30s (vezi handleTimeUpdate) — permitem acumulare legitima chiar si
      // cand timeupdate a fost suspendat temporar.
      if (delta > 0 && delta < 30) totalListenedDurationRef.current += delta;
      playStartTimeRef.current = null;
    }
    const a = audioRef.current;
    const medId = getTrackingMeditationId();
    if (a && medId) {
      const listenPct = a.duration ? Math.round((a.currentTime / a.duration) * 100) : 0;
      lastKnownListenPercentRef.current = Math.max(lastKnownListenPercentRef.current, listenPct);
      trackPlaySafe(medId, 'pause', {
        positionSec: Math.round(a.currentTime || 0),
        durationSec: Math.round(a.duration || lastDurationSecRef.current || 0),
        listenPercent: listenPct,
        listenedTotalSec: Math.round(totalListenedDurationRef.current),
        listenSessionId: lastListenSessionIdRef.current || undefined,
      });
    }
  }, [getTrackingMeditationId, trackPlaySafe]);

  const handleAudioError = useCallback((e) => {
    const a = e.currentTarget;
    const err = a?.error;
    const medId = getTrackingMeditationId();
    const rawSrc = a?.getAttribute('src');

    // Ignorăm zgomotul de la `src` gol/lipsă. Apare când resetăm audio-ul între
    // meditații — nu e o eroare reală de încărcare.
    const isEmptySrcNoise =
      !rawSrc || (err?.code === 4 && /empty src/i.test(err?.message || ''));
    if (isEmptySrcNoise) return;

    const info = {
      code: err?.code || null,
      message: err?.message || null,
      networkState: a?.networkState,
      readyState: a?.readyState,
      src: a?.currentSrc || rawSrc || null,
      meditationId: medId,
    };
    console.error('[audio] load error:', info);
    try {
      Sentry.captureMessage('audio_load_error', { level: 'error', extra: info });
    } catch (_) {}
  }, [getTrackingMeditationId]);

  const handleEnded = useCallback(() => {
    handlePause();
    sendMeditationClosed();

    if (user && !user.premium) {
      const currentMed = currentMeditationObjRef.current;
      const isFreeContent = currentMed && (currentMed.premium !== true && currentMed.premium !== 'Da');
      if (isFreeContent) {
        const count = parseInt(localStorage.getItem('nativus_free_completions') || '0', 10) + 1;
        localStorage.setItem('nativus_free_completions', String(count));
        if (count === 2) {
          setTimeout(() => triggerPremiumPopup(), 10000);
        }
      }
    }

    currentMeditationIdRef.current = null;
    setCurrentMeditation(null);
  }, [handlePause, sendMeditationClosed, user, triggerPremiumPopup]);

  const value = {
    audioRef,
    currentMeditation,
    tehniciMapGlobal,
    isPlaying,
    progress,
    currentTime,
    duration,
    audioUrl,
    audioError,
    feedbackReadyForId,
    setFeedbackReadyForId,
    loadMeditation,
    playMeditation,
    togglePlay,
    skip,
    seekTo,
    stopAndClear,
    formatDuration,
    sendMeditationClosed,
    completedTrackedRef,
  };

  return (
    <PlayerContext.Provider value={value}>
      {children}
      {/*
        NU controlăm `src` prin React. Îl setăm imperativ via
        `audioRef.current.src = url` după ce avem URL-ul semnat din backend.
        Altfel, un `src=""` la primul render e interpretat de browser ca
        URL-ul paginii curente și declanșează un MEDIA_ELEMENT_ERROR inutil.
      */}
      <audio
        ref={audioRef}
        preload="auto"
        style={{ display: 'none' }}
        onLoadedMetadata={handleLoadedMetadata}
        onLoadedData={handleLoadedData}
        onTimeUpdate={handleTimeUpdate}
        onPlay={handlePlay}
        onPause={handlePause}
        onEnded={handleEnded}
        onError={handleAudioError}
        onStalled={() => console.warn('[audio] stalled')}
      />
    </PlayerContext.Provider>
  );
};

export default PlayerContext;
